import {ApolloClient} from '@apollo/client';

import {
  MetaobjectOverrides,
  TranslatableResourceInformation,
} from '../../types';
import TranslatableProductQuery, {
  TranslatableProductQueryData,
} from '../../graphql/TranslatableProductQuery.graphql';
import TranslatableMenuQuery, {
  TranslatableMenuQueryData,
} from '../../graphql/TranslatableMenuQuery.graphql';
import MetafieldsQuery, {
  MetafieldsQueryData,
} from '../../graphql/MetafieldsQuery.graphql';
import MetaobjectQuery, {
  MetaobjectQueryData,
} from '../../graphql/MetaobjectQuery.graphql';
import TranslatableFilterQuery, {
  TranslatableFilterQueryData,
} from '../../graphql/TranslatableFilterQuery.graphql';
import TranslatableDeliveryMethodDefinitionQuery, {
  TranslatableDeliveryMethodDefinitionQueryData,
} from '../../graphql/TranslatableDeliveryMethodDefinitionQuery.graphql';

import {
  getMetafieldResourceInfo,
  getOptionValuesResourceInfo,
  getOptionsResourceInfo,
  getVariantsResourceInfo,
} from './utils';
import {Metafield} from './types';

interface MenuItem {
  id: string;
  title: string;
  items: MenuItem[];
}

export const fetchProductResourceIds = async (
  apollo: ApolloClient<unknown>,
  resourceId: string,
) => {
  const productInfo: TranslatableResourceInformation[] = [];

  const {data: translatableProductQueryData} = await apollo.query<
    TranslatableProductQueryData,
    TranslatableProductQueryData.Variables
  >({
    query: TranslatableProductQuery,
    variables: {
      id: resourceId,
    },
    fetchPolicy: 'network-only',
  });

  if (translatableProductQueryData.product) {
    const {
      product: {options, variants, hasOnlyDefaultVariant},
    } = translatableProductQueryData;
    const optionsInfo = hasOnlyDefaultVariant
      ? []
      : getOptionsResourceInfo(options);

    const optionValuesInfo = hasOnlyDefaultVariant
      ? []
      : getOptionValuesResourceInfo(options);
    const {variantsMetafieldsInfo} = getVariantsResourceInfo(variants);

    productInfo.push(
      ...optionsInfo,
      ...optionValuesInfo,
      ...variantsMetafieldsInfo,
    );
  }

  return productInfo;
};

export const fetchDeliveryMethodDefinitions = async (
  apollo: ApolloClient<unknown>,
  resourceId: string,
) => {
  const deliveryMethodInfo: TranslatableResourceInformation[] = [];

  const {data: deliveryMethodQueryData} = await apollo.query<
    TranslatableDeliveryMethodDefinitionQueryData,
    TranslatableDeliveryMethodDefinitionQueryData.Variables
  >({
    query: TranslatableDeliveryMethodDefinitionQuery,
    variables: {
      profileId: resourceId,
    },
    fetchPolicy: 'network-only',
  });

  deliveryMethodQueryData.deliveryProfileLocationGroups?.forEach((group) => {
    group.locationGroupZones?.forEach((zone) => {
      zone.methodDefinitions?.forEach((methodDefinition) => {
        deliveryMethodInfo.push({resourceId: methodDefinition.id});
      });
    });
  });

  return deliveryMethodInfo;
};

export const fetchMenuItemResourceIds = async (
  apollo: ApolloClient<unknown>,
  resourceId: string,
) => {
  const menuInfo: TranslatableResourceInformation[] = [];
  const menuItemResourceIds = (
    menuItems: MenuItem[],
  ): TranslatableResourceInformation[] => {
    let allMenuItemResourceIds: TranslatableResourceInformation[] = [];
    if (!menuItems || menuItems.length === 0) {
      return [];
    } else {
      menuItems.forEach((menuItem) => {
        allMenuItemResourceIds.push({resourceId: menuItem.id});
        if (menuItem.items) {
          allMenuItemResourceIds = [
            ...allMenuItemResourceIds,
            ...menuItemResourceIds(menuItem.items),
          ];
        }
      });
    }

    return allMenuItemResourceIds;
  };

  const {data: translatableMenuQueryData} = await apollo.query<
    TranslatableMenuQueryData,
    TranslatableMenuQueryData.Variables
  >({
    query: TranslatableMenuQuery,
    variables: {
      id: resourceId,
    },
    fetchPolicy: 'network-only',
  });

  if (translatableMenuQueryData.menu) {
    const {menu} = translatableMenuQueryData;
    menuInfo.push(...menuItemResourceIds(menu.items));
  }

  return menuInfo;
};

export const fetchMetafieldResourceIds = async (
  apollo: ApolloClient<unknown>,
  resourceId: string,
) => {
  if (!resourceId) return [];

  const {data: metafieldsQueryData} = await apollo.query<
    MetafieldsQueryData,
    MetafieldsQueryData.Variables
  >({
    query: MetafieldsQuery,
    variables: {
      ownerId: resourceId,
    },
    fetchPolicy: 'network-only',
  });

  const metafieldsInfo = getMetafieldResourceInfo(
    metafieldsQueryData?.metafields as Metafield[],
  );

  return metafieldsInfo;
};

export const fetchMetaobjectOverrides = async (
  apollo: ApolloClient<unknown>,
  resourceId: string,
) => {
  if (!resourceId) return {metaobjectOverrides: []};

  const {data: metaobjectQueryData} = await apollo.query<
    MetaobjectQueryData,
    MetaobjectQueryData.Variables
  >({
    query: MetaobjectQuery,
    variables: {
      id: resourceId,
    },
    fetchPolicy: 'network-only',
  });

  const {handle, fields, definition} = metaobjectQueryData?.metaobject;

  const metaobjectOverrides: MetaobjectOverrides[] = fields.map(
    ({definition, type, key}) => ({
      type,
      validations: definition?.validations.map(({value}) => value ?? '') ?? [],
      name: definition?.name ?? `${handle}.${key}`,
      key,
    }),
  );

  return {metaobjectOverrides, definition};
};

export const fetchFilterResourceIds = async (
  apollo: ApolloClient<unknown>,
  resourceId: string,
) => {
  const {data: translatableProductQueryData} = await apollo.query<
    TranslatableFilterQueryData,
    TranslatableFilterQueryData.Variables
  >({
    query: TranslatableFilterQuery,
    variables: {
      id: resourceId,
    },
    fetchPolicy: 'network-only',
  });

  return (
    translatableProductQueryData?.filterSetting?.groups.map((group) => ({
      resourceId: group.id,
    })) ?? []
  );
};
